import { useState } from "react";
import Swal from "sweetalert2";


export default function LeadForm() {

    const API_URL = "https://r5bkhvy0f6.execute-api.us-east-1.amazonaws.com/prod/automation/webhook/trigger/e6bed819ab";

    interface FormState {
        firstName: string;
        lastName: string;
        phone: string;
        email: string;
    }

    const [form, setForm] = useState<FormState>({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<Partial<FormState>>({});

    function validate(): boolean {
        const next: Partial<FormState> = {};
        if (!form.firstName.trim()) next.firstName = "First name is required";
        if (!form.lastName.trim()) next.lastName = "Last name is required";
        if (!form.phone.trim()) next.phone = "Phone number is required";
        if (!form.email.trim()) next.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(form.email))
            next.email = "Enter a valid email";
        setErrors(next);
        return Object.keys(next).length === 0;
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {

            Swal.fire({
                position: 'center',
                title: 'Processing...',
                html: 'Please wait while we process your request',
                allowEscapeKey: false,
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const res = await fetch(API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    firstName: form.firstName,
                    lastName: form.lastName,
                    email: form.email,
                    phone: form.phone,
                }),
            });
            const data = await res.json();
            if (
                data?.message === "Execution Successful" ||
                data === "Execution Successful"
            ) {

                setForm({ firstName: "", lastName: "", phone: "", email: "" });
                setErrors({});
                Swal.fire({
                    position: 'center',
                    icon: 'success',
                    title: "You're all set!",
                    text: 'Thanks, we will contact you shortly.',
                    showConfirmButton: false,
                    timer: 3000,
                });
            } else {

                await Swal.fire({
                    position: 'center',
                    icon: 'error',
                    title: `Something went wrong`,
                    text: "Please try again or call us at (541) 600-3371.",
                    confirmButtonColor: "oklch(0.22 0.06 255)",
                });
            }
        } catch {
            await Swal.fire({
                position: 'center',
                icon: 'error',
                title: `Something went wrong`,
                text: "Please try again or call us at (541) 600-3371.",
                confirmButtonColor: "oklch(0.22 0.06 255)",
            });
        } finally {
            setSubmitting(false);
        }
    }

    function handleChange(field: keyof FormState, value: string) {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    }

    return (
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    <label
                        htmlFor="jj-firstName"
                        className="text-xs font-body font-semibold text-hero-foreground uppercase tracking-wide block"
                    >
                        First Name <span className="text-gold">*</span>
                    </label>
                    <input
                        id="jj-firstName"
                        type="text"
                        autoComplete="given-name"
                        value={form.firstName}
                        onChange={(e) =>
                            handleChange("firstName", e.target.value)
                        }
                        placeholder="John"
                        className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-hero-foreground placeholder:text-hero-foreground placeholder:opacity-50 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-smooth"
                        data-ocid="jj-first-name"
                        required
                    />
                    {errors.firstName && (
                        <p className="text-xs text-gold">{errors.firstName}</p>
                    )}
                </div>
                <div className="space-y-1.5">
                    <label
                        htmlFor="jj-lastName"
                        className="text-xs font-body font-semibold text-hero-foreground uppercase tracking-wide block"
                    >
                        Last Name <span className="text-gold">*</span>
                    </label>
                    <input
                        id="jj-lastName"
                        type="text"
                        autoComplete="family-name"
                        value={form.lastName}
                        onChange={(e) => handleChange("lastName", e.target.value)}
                        placeholder="Doe"
                        className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-hero-foreground placeholder:text-hero-foreground placeholder:opacity-50 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-smooth"
                        data-ocid="jj-last-name"
                        required
                    />
                    {errors.lastName && (
                        <p className="text-xs text-gold">{errors.lastName}</p>
                    )}
                </div>
            </div>

            <div className="space-y-1.5">
                <label
                    htmlFor="jj-phone"
                    className="text-xs font-body font-semibold text-hero-foreground uppercase tracking-wide block"
                >
                    Phone <span className="text-gold">*</span>
                </label>
                <input
                    id="jj-phone"
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="(541) 000-0000"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-hero-foreground placeholder:text-hero-foreground placeholder:opacity-50 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-smooth"
                    data-ocid="jj-phone"
                    required
                />
                {errors.phone && (
                    <p className="text-xs text-gold">{errors.phone}</p>
                )}
            </div>

            <div className="space-y-1.5">
                <label
                    htmlFor="jj-email"
                    className="text-xs font-body font-semibold text-hero-foreground uppercase tracking-wide block"
                >
                    Email <span className="text-gold">*</span>
                </label>
                <input
                    id="jj-email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-hero-foreground placeholder:text-hero-foreground placeholder:opacity-50 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-smooth"
                    data-ocid="jj-email"
                    required
                />
                {errors.email && (
                    <p className="text-xs text-gold">{errors.email}</p>
                )}
            </div>

            <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 font-display font-bold text-base bg-gradient-accent text-foreground shadow-gold hover:opacity-90 active:scale-[0.98] transition-smooth border-0 rounded-xl cursor-pointer"
                data-ocid="jj-submit"
            >
                {submitting ? "Sending…" : "Book Your Free Class"}
            </button>

            <p className="text-center text-xs text-hero-foreground opacity-50 mt-3">
                No commitment required. Cancel anytime.
            </p>
        </form>
    )

}